import * as React from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';

import { downloadFile } from '../../../lib/download';
import { cn } from '../../../lib/utils';
import { Carousel, CarouselContent, CarouselItem, useCarousel } from '../../ui/carousel';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import type { GalleryImage } from './image-gallery';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ImageLightboxProps {
  /** Accessible label for the close control. */
  closeAriaLabel: string;
  /** Resolves the accessible label for the download button of a given image index. */
  downloadAriaLabel: (index: number) => string;
  /** Optional `title` attribute shared by every download button. */
  downloadTitle?: string;
  images: GalleryImage[];
  /** Slide shown when the lightbox first opens. */
  initialIndex: number;
  /** Accessible label for the "next photo" control. */
  nextAriaLabel: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /** Accessible label for the "previous photo" control. */
  previousAriaLabel: string;
  /** Visually-hidden dialog title required for accessibility (no visible header in the lightbox). */
  title: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CHIP_CLASS_NAME =
  'absolute inline-flex items-center justify-center bg-background/90 text-foreground shadow-sm hover:bg-background';

// ─── Sub-components ───────────────────────────────────────────────────────────

interface LightboxControlsProps {
  readonly closeAriaLabel: string;
  readonly nextAriaLabel: string;
  readonly onClose: () => void;
  readonly previousAriaLabel: string;
  readonly showNavigation: boolean;
}

/**
 * Close chip + prev/next chips floating over the current slide. Rendered once
 * as a sibling of `CarouselContent` (not per-slide, unlike the per-image
 * download chip) so exactly one of each control exists in the DOM regardless
 * of how many images the gallery has — positioned against `Carousel`'s own
 * `relative` root, which spans the currently visible slide.
 */
function LightboxControls({
  closeAriaLabel,
  nextAriaLabel,
  onClose,
  previousAriaLabel,
  showNavigation,
}: LightboxControlsProps): React.JSX.Element {
  const { canScrollNext, canScrollPrev, scrollNext, scrollPrev } = useCarousel();

  return (
    <>
      <button
        aria-label={closeAriaLabel}
        className={cn(
          CHIP_CLASS_NAME,
          'end-2 top-2 h-8 w-8 rounded-md focus:outline-none focus:ring-2 focus:ring-primary',
        )}
        onClick={onClose}
        type="button"
      >
        <X className="h-4 w-4" />
      </button>
      {showNavigation && (
        <>
          <button
            aria-label={previousAriaLabel}
            className={cn(
              CHIP_CLASS_NAME,
              'start-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full bg-background/80 disabled:pointer-events-none disabled:opacity-50',
            )}
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            type="button"
          >
            <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
          </button>
          <button
            aria-label={nextAriaLabel}
            className={cn(
              CHIP_CLASS_NAME,
              'end-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full bg-background/80 disabled:pointer-events-none disabled:opacity-50',
            )}
            disabled={!canScrollNext}
            onClick={scrollNext}
            type="button"
          >
            <ChevronRight className="h-5 w-5 rtl:rotate-180" />
          </button>
        </>
      )}
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Full-size image viewer opened by clicking a thumbnail in `ImageGallery`.
 * Minimal chrome: the shadcn `DialogContent` frame (background, border,
 * padding, shadow) is stripped via `className` overrides, and its built-in
 * default close button is omitted (`hideCloseButton`) in favor of our own
 * custom-placed close chip — so only the image and a set of floating control
 * chips (close, prev/next, per-image download) are visible, with no dialog
 * card around the photo. It is still a Radix `Dialog` under the hood for
 * focus trapping, Escape-to-close, and the click-outside overlay; a
 * visually-hidden `DialogTitle` satisfies Radix's accessibility requirement
 * without rendering any visible header.
 *
 * Embla-powered carousel with prev/next chips (hidden for a single image) and
 * a per-slide download chip. Generic — carries no domain-specific text beyond
 * what is passed in via props.
 */
export function ImageLightbox({
  closeAriaLabel,
  downloadAriaLabel,
  downloadTitle,
  images,
  initialIndex,
  nextAriaLabel,
  onOpenChange,
  open,
  previousAriaLabel,
  title,
}: ImageLightboxProps): React.JSX.Element {
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-2xl gap-0 border-0 bg-transparent p-0 shadow-none" hideCloseButton>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <Carousel className="w-full" opts={{ startIndex: initialIndex }}>
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={image.src}>
                <div className="relative">
                  <img alt={image.alt} className="h-auto max-h-[85vh] w-full rounded object-contain" src={image.src} />
                  <button
                    aria-label={downloadAriaLabel(index)}
                    className={cn(CHIP_CLASS_NAME, 'end-11 top-2 h-8 w-8 rounded-md')}
                    onClick={(): void => {
                      void downloadFile(image.src, image.downloadFileName);
                    }}
                    title={downloadTitle}
                    type="button"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <LightboxControls
            closeAriaLabel={closeAriaLabel}
            nextAriaLabel={nextAriaLabel}
            onClose={(): void => onOpenChange(false)}
            previousAriaLabel={previousAriaLabel}
            showNavigation={images.length > 1}
          />
        </Carousel>
      </DialogContent>
    </Dialog>
  );
}
